import { collabStateManager } from '@databases/collab-state-manager';
import { convertToYjs } from '@databases/collab/convert/to-collab';
import { YDatabase } from '@databases/collab/y-database';
import { getActivationId, getCloudId, getVersionId } from '@databases/database-state';
import { resetDatabaseLoadingState } from '@databases/database-state/state/initial-database-loading-state';
import { logs } from '@databases/logs/activity-logger';
import { type LogEntry } from '@databases/logs/types';
import { type DatabaseJSON } from '@databases/model/database';
import { runDatabaseHydrationDispatches } from '@databases/redux';
import {
  buildDatabaseFromCollabAndContent,
  buildDefaultValueConverters,
  createCollabManager,
  syncCollabChangesToRedux
} from '@databases/redux/collab/actions';
import { appStore } from '@databases/redux/store';
import { container } from '@databases/service-container';
import databases from '@databases/testing/databases';
import { type Database } from '@databases/types/database';
import {
  addDatabaseToList,
  fakeDatabaseContent,
  markDatabaseAsLoaded
} from '@databases/utils/redux/databases/actions';
import { snapshot } from 'valtio';

import { currentHostFrameContext } from '@canvas/canvas-state/host-api';
import {
  type ClientFeatureFlagName,
  isFeatureFlagEnabled,
  setFeatureFlagOverride
} from '@canvas/client-feature-flags';
import {
  addOverrides,
  getOverrides
} from '@canvas/client-feature-flags/overrides/persisted';
import { Action } from '@canvas/model/permissions';

import { type GetDatabaseTestPermissionsOverride } from './test-permissions-contract.ts';

export type DatabaseName = keyof typeof databases;

export type SetDatabaseOptions =
  | {
      databaseName?: DatabaseName;
    }
  | { contentId: string; json: DatabaseJSON };

const validUUID = '4d555e81-af99-4dfa-9073-db4e49df4f6e';
const DATABASE_TEST_PERMISSIONS_MARKER = '__databasesTestPermissions';

/**
 * Checks only whether Playwright explicitly requested the private test fixture.
 *
 * SECURITY BOUNDARY: marker presence is untrusted and grants nothing. This check exists only to
 * avoid loading permission-override code for unrelated integration tests. The private module still
 * validates the marker value, build mode, and local host before constructing an override. Keep the
 * exact compile-time mode check so non-test builds remove this branch; bundle guards fail if the
 * marker survives in emitted non-test JavaScript.
 */
const isDatabaseTestPermissionsOverrideRequested = () =>
  process.env.MODE === 'test' &&
  Object.prototype.hasOwnProperty.call(globalThis, DATABASE_TEST_PERMISSIONS_MARKER);

/**
 * Loads the private permission fixture only in a compile-time test build.
 *
 * SECURITY BOUNDARY: the build replaces `process.env.MODE` with a string literal and aliases this
 * dynamic import to a fail-closed module in every non-test build before Vite or esbuild can resolve
 * the private implementation. Keep this exact equality check directly around the import. Both
 * bundlers also fail independently if a private permission-override sentinel reaches emitted
 * JavaScript.
 *
 * Do not statically import the module, broaden the mode check, or expose the override through
 * `databaseState`, the service container, bridge data, URL parameters, storage, or product code.
 */
const loadDatabaseTestPermissionsOverride = async () => {
  if (process.env.MODE !== 'test') {
    return undefined;
  }

  const testPermissionsModule = (await import('./test-permissions.ts')) as unknown as {
    default: GetDatabaseTestPermissionsOverride;
  };

  return testPermissionsModule.default();
};

const setFlagOverride = (flag: string, value: boolean) => {
  addOverrides({ [flag]: value });
  setFeatureFlagOverride(getOverrides());

  const eventBus = container.resolve('EventBus');
  eventBus.dispatch('featureFlagsChanged', { [flag]: value });
};

const setPageExportStatus = (status: boolean) => {
  currentHostFrameContext.data.dataSecurityPolicy = {
    ...currentHostFrameContext.data.dataSecurityPolicy,
    pageExport: status
  };
};

const getFlagValue = (flag: ClientFeatureFlagName) => {
  return isFeatureFlagEnabled(flag);
};

const getIsDatabaseReadOnly = (database: Database) =>
  !database.operations.delete &&
  !database.operations.updateEntries &&
  !database.operations.updateMetadata;

const setDatabase = async (options: SetDatabaseOptions) => {
  resetDatabaseLoadingState();

  const testPermissionsOverride = isDatabaseTestPermissionsOverrideRequested()
    ? await loadDatabaseTestPermissionsOverride()
    : undefined;
  let database: Database;
  let contentId;

  let doc;

  if ('json' in options) {
    doc = YDatabase.fromJSON(options.json, {
      getCurrentUser: () => {
        throw new Error();
      }
    });
    contentId = options.contentId;
    database = buildDatabaseFromCollabAndContent(
      {
        id: contentId,
        referenceId: '1234',
        space: { id: 12345, key: 'ABC', name: 'ABC' },
        type: 'database',
        title: 'my database',
        ...fakeDatabaseContent
      },
      doc,
      false
    );
  } else {
    let databaseName: keyof typeof databases = 'emptyDatabase';
    if ('databaseName' in options && options.databaseName) {
      databaseName = options.databaseName;
    }

    const selectedDatabase = databases[databaseName];
    database = selectedDatabase.database;
    contentId = database.contentId ?? selectedDatabase.contentId;
    doc = convertToYjs(database, buildDefaultValueConverters(), {});
  }

  database = testPermissionsOverride?.applyToDatabase(database) ?? database;

  const isReadOnly = getIsDatabaseReadOnly(database);

  runDatabaseHydrationDispatches(() => {
    appStore.dispatch(addDatabaseToList(database));
    appStore.dispatch(markDatabaseAsLoaded(contentId));
  });

  const cloudId = getCloudId();
  const activationId = getActivationId();
  const versionId = getVersionId();

  const collabManager = createCollabManager({
    contentId,
    spaceId: 1234,
    referenceId: validUUID,
    initialDatabase: doc,
    cloudId,
    activationId,
    versionId
  });

  collabManager.database.doc.on(
    'update',
    syncCollabChangesToRedux(collabManager, appStore.dispatch, {
      contentId,
      isReadOnly
    })
  );

  collabManager.setGrantsWithoutSync(
    new Set(isReadOnly ? [Action.read] : [Action.read, Action.write])
  );

  // Integration tests have no real backend, so manager.connect() (called later
  // by loadDatabaseByReferenceId) tries to fetch a token, fails, and resets
  // the token-manager claims to an empty set. That reset fires
  // onPermissionsChanged(isReadOnly=true), which the bootstrap's
  // wireCollabManager then forwards to PermissionsManager.setOperations,
  // clobbering the explicit grants set above. Keep connect as a no-op for every
  // app-access fixture; this predates and is independent of the opt-in
  // permission override. The override separately stabilizes the direct token()
  // consumer only when Playwright installed the private marker. This state is
  // loaded by `@databases/app-access/init`, which is test/dev-only in practice
  // despite being imported by the production app: product code never calls
  // `databaseState.setDatabase`.
  collabManager.connect = async () => {};

  testPermissionsOverride?.stabilizeTokenManager(collabManager);

  databaseState.setContentId(contentId);

  return database;
};

const addDatabase = (databaseName: DatabaseName) => {
  const selectedDatabase = databases[databaseName];
  runDatabaseHydrationDispatches(() => {
    appStore.dispatch(addDatabaseToList(selectedDatabase.database as Database));
    appStore.dispatch(markDatabaseAsLoaded(selectedDatabase.contentId));
  });
};

export type DatabaseState = {
  reactAppIsReady: boolean;
  setContentId: (contentId: string) => void;
  setDatabase: (options: SetDatabaseOptions) => Promise<Database>;
  addDatabase: (databaseName: DatabaseName) => void;
  getLogs: () => readonly LogEntry[];
  setFlagOverride: (flag: string, value: boolean) => void;
  getFlagValue: (flag: ClientFeatureFlagName) => boolean;
  getDatabaseJSON: (contentId: string) => DatabaseJSON | undefined;
  setPageExportStatus: (status: boolean) => void;
};

export const databaseState: DatabaseState = {
  reactAppIsReady: false,
  setDatabase,
  addDatabase,
  setContentId: () => {},
  getLogs: () => snapshot(logs),
  setFlagOverride,
  getFlagValue,
  getDatabaseJSON: (contentId: string) =>
    collabStateManager.getCollabManager(contentId)?.database.toJSON(),
  setPageExportStatus
};
